import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Grid2, InputAdornment, TextField } from "@mui/material";
import { observer } from "mobx-react";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import {
  SwayyProductSort,
  SwayyProductStatus,
  SwayySalesChannel,
} from "../../../models/products/SwayyProduct";
import { SwayyProductsStore } from "../../../stores/SwayyProductsStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";

const STATUS_OPTIONS: FilterValue[] = [
  { id: "draft", value: "Draft" },
  { id: "live", value: "Live" },
  { id: "archived", value: "Archived" },
];

const CHANNEL_OPTIONS: FilterValue[] = [
  { id: "B2C", value: "B2C" },
  { id: "B2B", value: "B2B" },
];

const SORT_OPTIONS: FilterValue[] = [
  { id: "newest", value: "Newest" },
  { id: "name", value: "Name" },
  { id: "updated", value: "Recently updated" },
];

interface SwayyProductFiltersProps {
  store: SwayyProductsStore;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClearFilters: () => void;
  disabled?: boolean;
  compact?: boolean;
}

const selectedOptions = (options: FilterValue[], ids: string[]) =>
  options.filter((option) => ids.includes(option.id));

const dropdownStyles = {
  accentColor: swayyColors.pine,
  backgroundColor: swayyColors.white,
  borderColor: swayyColors.border,
  borderRadius: swayyRadii.sm,
  inputHeight: "42px",
  local: true,
} as const;

const SwayyProductFilters = ({
  store,
  searchValue,
  onSearchChange,
  onClearFilters,
  disabled = false,
  compact = false,
}: SwayyProductFiltersProps) => (
  <Grid2 container spacing={1.5} sx={{ width: "100%" }}>
    <Grid2 size={{ xs: 12, md: compact ? 12 : 4, lg: compact ? 12 : 3 }}>
      <TextField
        fullWidth
        size="small"
        value={searchValue}
        disabled={disabled}
        placeholder="Search by product name or description"
        onChange={(event) => onSearchChange(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  sx={{ color: swayyColors.textSecondary, fontSize: 20 }}
                />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": { minHeight: 42 },
          "& input": { fontSize: 13 },
        }}
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: compact ? 12 : 2 }}>
      <AppSearchDropdownMulti
        {...dropdownStyles}
        title="Status"
        isMultiSelect={false}
        list={STATUS_OPTIONS}
        value={selectedOptions(STATUS_OPTIONS, [store.filters.status])}
        disabled={disabled}
        handleSelect={(items) => {
          const status = items[0]?.id as SwayyProductStatus | undefined;
          if (status) store.setStatus(status);
        }}
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: compact ? 12 : 2 }}>
      <AppSearchDropdownMulti
        {...dropdownStyles}
        title="Brand"
        isMultiSelect={false}
        list={store.brandOptions}
        value={selectedOptions(store.brandOptions, [
          String(store.filters.brandId ?? ""),
        ])}
        disabled={disabled || store.isLoadingFilterOptions}
        handleSelect={(items) =>
          store.setBrandId(items[0]?.id ? Number(items[0].id) : null)
        }
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: compact ? 12 : 2 }}>
      <AppSearchDropdownMulti
        {...dropdownStyles}
        title="Category"
        isMultiSelect={false}
        list={store.categoryOptions}
        value={selectedOptions(store.categoryOptions, [
          String(store.filters.categoryId ?? ""),
        ])}
        disabled={disabled || store.isLoadingFilterOptions}
        handleSelect={(items) =>
          store.setCategoryId(items[0]?.id ? Number(items[0].id) : null)
        }
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: compact ? 12 : 2, lg: 1.5 }}>
      <AppSearchDropdownMulti
        {...dropdownStyles}
        title="Sales channel"
        list={CHANNEL_OPTIONS}
        value={selectedOptions(CHANNEL_OPTIONS, store.filters.channels)}
        disabled={disabled}
        handleSelect={(items) =>
          store.setChannels(items.map((item) => item.id as SwayySalesChannel))
        }
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: compact ? 12 : 2, lg: 1.5 }}>
      <AppSearchDropdownMulti
        {...dropdownStyles}
        title="Sort"
        isMultiSelect={false}
        list={SORT_OPTIONS}
        value={selectedOptions(SORT_OPTIONS, [store.filters.sortBy])}
        disabled={disabled}
        handleSelect={(items) => {
          const sortBy = items[0]?.id as SwayyProductSort | undefined;
          if (sortBy) store.setSortBy(sortBy);
        }}
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: compact ? 12 : "auto" }}>
      <AppButton
        variant="transparentPrimary"
        label="Clear filters"
        disabled={disabled || !store.hasActiveFilters}
        onClick={onClearFilters}
        customStyles={{
          width: compact ? "100%" : "auto",
          minHeight: 42,
          color: swayyColors.deepGreen,
          border: `1px solid ${swayyColors.border}`,
          borderRadius: swayyRadii.md,
          whiteSpace: "nowrap",
          "&:hover": {
            borderColor: swayyColors.pine,
            backgroundColor: swayyColors.pineLight,
          },
        }}
      />
    </Grid2>
  </Grid2>
);

export default observer(SwayyProductFilters);
